/**
 * LeadForge France - API Client
 */
import axios from 'axios';

const API_BASE = 'https://leadforgefrance.com/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('leadforge_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Default export
export default api;

// Named exports
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getMe: () => api.get('/auth/me'),
};

export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
};

export const geographyAPI = {
  getDepartments: () => api.get('/geography/departments'),
  getRegions: () => api.get('/geography/regions'),
  getAll: () => Promise.all([
    api.get('/geography/regions'),
    api.get('/geography/departments')
  ]).then(([regions, departments]) => ({
    data: {
      regions: regions.data,
      departments: departments.data
    }
  })),
};

export const pricingAPI = {
  getPlans: () => api.get('/pricing/plans'),
  getPricing: () => api.get('/pricing'),
  getTiers: () => api.get('/pricing/tiers'),
  calculate: (data) => api.post('/pricing/calculate', data),
};

export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  getAll: () => api.get('/orders'),
  list: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  download: (id) => api.get(`/orders/${id}/download`, { responseType: 'blob' }),
};

export const checkoutAPI = {
  createSession: (data) => api.post('/stripe/create-checkout-session', data),
  create: (data) => api.post('/checkout/create', data),
  status: (sessionId) => api.get(`/checkout/status/${sessionId}`),
};

export const configAPI = {
  get: () => api.get('/config'),
  update: (data) => api.put('/config', data),
};

export const contactAPI = {
  submit: (data) => api.post('/contact', data),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getOrders: () => api.get('/dashboard/orders'),
};

export const seoAPI = {
  getPage: (slug) => api.get(`/seo/pages/${slug}`),
  getPages: () => api.get('/seo/pages'),
};

// Aperçu de leads (RGPD-safe) : l'endpoint ne renvoie jamais email/téléphone,
// seulement des booléens de disponibilité. Erreur -> null (ne casse pas la page).
export const getLeadsPreview = async ({ categorySlug, geoScope = 'FR', geoValue = 'FR', limit = 5 }) => {
  try {
    const res = await api.get('/leads/preview', {
      params: { category_slug: categorySlug, geo_scope: geoScope, geo_value: geoValue, limit },
    });
    return res.data;
  } catch (e) {
    return null;
  }
};

// Enrichment Engine P0 (2026-09-19) -- admin-only cote serveur (get_current_admin
// + ENRICHMENT_ENGINE_V1), reutilise le token JWT deja injecte par l'intercepteur.
export const enrichmentAPI = {
  // Resultat existant (cache), 404 si jamais enrichi -- ne relance jamais le pipeline.
  getCached: (siret) => api.get(`/admin/enrichment/${siret}`),
  // Lance/relance le pipeline complet ("Enrichir" ou "Actualiser").
  run: (siret) => api.post(`/admin/enrichment/${siret}`),
  status: () => api.get('/admin/enrichment/status'),
};

// Batch Admin V1 (suite 2026-09-20) -- meme garde-fou serveur que ci-dessus
// (admin-only + ENRICHMENT_ENGINE_V1), aucun nouveau systeme d'auth cote client.
export const enrichmentBatchAPI = {
  preview: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/admin/enrichment/batch/preview', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  create: (sirets, sourceFilename) => api.post('/admin/enrichment/batch', { sirets, source_filename: sourceFilename }),
  list: () => api.get('/admin/enrichment/batch'),
  get: (batchId) => api.get(`/admin/enrichment/batch/${batchId}`),
  items: (batchId, status) => api.get(`/admin/enrichment/batch/${batchId}/items`, { params: status ? { status } : {} }),
  start: (batchId) => api.post(`/admin/enrichment/batch/${batchId}/start`),
  pause: (batchId) => api.post(`/admin/enrichment/batch/${batchId}/pause`),
  resume: (batchId) => api.post(`/admin/enrichment/batch/${batchId}/resume`),
  cancel: (batchId) => api.post(`/admin/enrichment/batch/${batchId}/cancel`),
  retryFailed: (batchId) => api.post(`/admin/enrichment/batch/${batchId}/retry-failed`),
  // responseType 'blob' + Authorization portee par l'intercepteur existant
  // (un <a href> brut ne porterait pas le JWT -- endpoint admin-only).
  export: (batchId, format) => api.get(`/admin/enrichment/batch/${batchId}/export`, {
    params: { format }, responseType: 'blob',
  }),
};
